import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { IAcabamento, ISelectOption } from "../types/acabamento.types";
import { RetornoMidia, RetornoNomeMidia, RetornoRemocaoMidia } from "../../types/midia.types";

// ===== CRUD =====

export const obterAcabamentoPorId = async (idAcabamento: number): Promise<IAcabamento | null> => {
  try {
    const { data } = await api.get(`/AcabamentoN1/ObterPorId/${idAcabamento}`);
    
    if (!data) return null;

    return {
      ...data,
      idTratamento: data.idTratamento,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter acabamento");
    return null;
  }
};

export const salvarAcabamento = async (acabamento: Partial<IAcabamento>, unidades?: ISelectOption[]): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const isEdicao = !!acabamento.idTratamento;
    const endpoint = isEdicao 
      ? `/AcabamentoN1/Atualizar/${acabamento.idTratamento}`
      : `/AcabamentoN1/Criar`;
    
    // Mapear campos para o formato esperado pelo backend (PascalCase)
    const dadosLimpos: any = {};
    
    // Campos básicos
    if (acabamento.idTratamento !== undefined) dadosLimpos.IdTratamento = acabamento.idTratamento;
    if (acabamento.idStatus !== undefined) dadosLimpos.IdStatus = acabamento.idStatus;
    if (acabamento.idSituacao !== undefined) dadosLimpos.IdSituacao = acabamento.idSituacao;
    if (acabamento.idReferencia !== undefined) dadosLimpos.IdReferencia = acabamento.idReferencia;
    if (acabamento.idTipoTratamento !== undefined) dadosLimpos.IdTipoTratamento = acabamento.idTipoTratamento;
    if (acabamento.codigo !== undefined) dadosLimpos.Codigo = acabamento.codigo;
    if (acabamento.codigoInterno !== undefined) dadosLimpos.CodigoInterno = acabamento.codigoInterno;
    if (acabamento.descricao !== undefined) dadosLimpos.Descricao = acabamento.descricao;
    if (acabamento.referencia !== undefined) dadosLimpos.Referencia = acabamento.referencia;
    if (acabamento.loteMinimo !== undefined) dadosLimpos.LoteMinimo = acabamento.loteMinimo;
    
    // Unidade — mapear idUnidade para IdUnidade e gravar a SIGLA.
    // FASE 05 — Ajustes 25/08 (item 2): `Tratamentos.Unidade` é a coluna que alimenta a tag
    // <UNID> do XML do CEM (XMLMaterialAcabamentoService). Aqui gravava-se `nome`, que a API
    // preenche com a DESCRIÇÃO (ReferenciaDadosController) — o pacote saía com "Kilos" no
    // lugar de "KG". Só a sigla serve; sem ela, preserva-se o valor já gravado.
    if (acabamento.idUnidade !== undefined && acabamento.idUnidade > 0) {
      dadosLimpos.IdUnidade = acabamento.idUnidade;
      if (unidades && unidades.length > 0) {
        const unidadeSelecionada = unidades.find(u => u.id === acabamento.idUnidade);
        dadosLimpos.Unidade = unidadeSelecionada?.sigla || acabamento.unidade || "";
      } else {
        dadosLimpos.Unidade = acabamento.unidade || "";
      }
    } else if (acabamento.unidade) {
      // Se não tem idUnidade mas tem unidade (string), usar apenas a string
      dadosLimpos.Unidade = acabamento.unidade;
    }
    
    // Textura/Cor
    if (acabamento.texturaCorHex !== undefined) dadosLimpos.TexturaCorHex = acabamento.texturaCorHex;
    if (acabamento.texturaCorRgb !== undefined) dadosLimpos.TexturaCorRgb = acabamento.texturaCorRgb;
    if (acabamento.texturaOpacidade !== undefined) dadosLimpos.TexturaOpacidade = acabamento.texturaOpacidade;
    if (acabamento.texturaFiguraBase64 !== undefined) dadosLimpos.TexturaFiguraBase64 = acabamento.texturaFiguraBase64;
    if (acabamento.texturaFiguraFormato !== undefined) dadosLimpos.TexturaFiguraFormato = acabamento.texturaFiguraFormato;
    if (acabamento.texturaFiguraAlterada !== undefined) dadosLimpos.TexturaFiguraAlterada = acabamento.texturaFiguraAlterada;
    if (acabamento.texturaPrioridadeVisual !== undefined) dadosLimpos.TexturaPrioridadeVisual = acabamento.texturaPrioridadeVisual;
    
    const { data } = await api[isEdicao ? 'put' : 'post'](endpoint, dadosLimpos);
    
    return {
      id: data.id || acabamento.idTratamento || 0,
      sucesso: data.sucesso || false,
      mensagem: data.mensagem || (isEdicao ? "Acabamento atualizado com sucesso" : "Acabamento criado com sucesso"),
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar acabamento");
    return null;
  }
};

export const excluirAcabamento = async (idAcabamento: number): Promise<boolean> => {
  try {
    const { data } = await api.delete(`/AcabamentoN1/Excluir/${idAcabamento}`);
    return data?.sucesso || false;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao excluir acabamento");
    return false;
  }
};

export const duplicarAcabamento = async (idAcabamento: number): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const { data } = await api.post(`/AcabamentoN1/Duplicar/${idAcabamento}`);
    
    return {
      id: data.id || 0,
      sucesso: data.sucesso || false,
      mensagem: data.mensagem || "Cópia criada. Revise os dados antes de salvar.",
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao duplicar acabamento");
    return null;
  }
};

export const validarCodigoUnicoAcabamento = async (codigo: string, idExcluir?: number): Promise<boolean> => {
  try {
    const params = new URLSearchParams({ codigo });
    if (idExcluir) params.append('idExcluir', idExcluir.toString());
    
    const { data } = await api.get(`/AcabamentoN1/ValidarCodigoUnico?${params.toString()}`);
    return data?.valido ?? false;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao validar código");
    return false;
  }
};

// ===== MÍDIAS =====

// Plano 21/09/2026 (Frente 3): as escritas de textura/cor devolvem também o `id` do registro gravado —
// com o acabamento Disponível, a API grava na cópia de trabalho e a tela precisa seguir para ela.
export const uploadFiguraAcabamento = async (idAcabamento: number, arquivo: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);
    
    const { data } = await api.post(`/AcabamentoN1/${idAcabamento}/Textura/UploadFigura`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    return data?.url ? { url: data.url, id: data.id ?? idAcabamento } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload da figura");
    return null;
  }
};

export const removerFiguraAcabamento = async (idAcabamento: number): Promise<RetornoRemocaoMidia | null> => {
  try {
    const { data } = await api.delete(`/AcabamentoN1/${idAcabamento}/Textura/RemoverFigura`);
    return data?.sucesso ? { sucesso: true, id: data.id ?? idAcabamento } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover figura");
    return null;
  }
};

export const salvarCorAcabamento = async (idAcabamento: number, corHex: string, corRgb: string, opacidade?: number): Promise<RetornoRemocaoMidia | null> => {
  try {
    const { data } = await api.post(`/AcabamentoN1/${idAcabamento}/Textura/SalvarCor`, {
      corHex,
      corRgb,
      opacidade,
    });
    
    return data?.sucesso ? { sucesso: true, id: data.id ?? idAcabamento } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar cor");
    return null;
  }
};

export const removerCorAcabamento = async (idAcabamento: number): Promise<RetornoRemocaoMidia | null> => {
  try {
    const { data } = await api.delete(`/AcabamentoN1/${idAcabamento}/Textura/RemoverCor`);
    return data?.sucesso ? { sucesso: true, id: data.id ?? idAcabamento } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover cor");
    return null;
  }
};

// ===== LISTAS DE REFERÊNCIA =====

export const listarSituacoes = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get("/ReferenciaDados/Situacoes/Listar");
    return (data || []).map((item: any) => ({
      id: item.id || item.Id,
      nome: item.nome || item.descricao || item.Nome || item.Descricao,
      descricao: item.descricao || item.Descricao || item.nome || item.Nome,
    }));
  } catch (error: any) {
    console.warn("Erro ao listar situações:", error);
    return [];
  }
};

export const listarStatus = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get("/ReferenciaDados/Status/Listar");
    return (data || []).map((item: any) => ({
      id: item.id || item.Id,
      nome: item.nome || item.descricao || item.Nome || item.Descricao,
      descricao: item.descricao || item.Descricao || item.nome || item.Nome,
    }));
  } catch (error: any) {
    console.warn("Erro ao listar status:", error);
    return [];
  }
};

export const listarUnidades = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get("/ReferenciaDados/Unidades");
    return (data || []).map((item: any) => ({
      id: item.id || item.Id,
      nome: item.nome || item.descricao || item.Nome || item.Descricao,
      descricao: item.descricao || item.Descricao || item.nome || item.Nome,
      // FASE 05 — Ajustes 25/08 (item 2): a sigla é o que vai para a tag <UNID> do XML.
      sigla: item.sigla || item.Sigla || "",
    }));
  } catch (error: any) {
    console.warn("Erro ao listar unidades:", error);
    return [];
  }
};

export const listarTiposAcabamento = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get("/ReferenciaDados/TiposAcabamento/Listar");
    return (data || []).map((item: any) => ({
      id: item.id || item.Id,
      nome: item.nome || item.descricao || item.Nome || item.Descricao,
      descricao: item.descricao || item.Descricao || item.nome || item.Nome,
    }));
  } catch (error: any) {
    console.warn("Erro ao listar tipos de acabamento:", error);
    return [];
  }
};

export const listarReferencias = async (idTipoTela?: number): Promise<ISelectOption[]> => {
  try {
    const params: any = {};
    if (idTipoTela) params.idTipoTela = idTipoTela;
    const { data } = await api.get("/ReferenciaDados/Referencias", { params });
    return (data || []).map((item: any) => ({
      id: item.idReferencia || item.IdReferencia || item.id || item.Id,
      nome: item.descricao || item.Descricao || item.nome || item.Nome,
      descricao: item.descricao || item.Descricao || item.nome || item.Nome,
    }));
  } catch (error: any) {
    console.warn("Erro ao listar referências:", error);
    return [];
  }
};

// ===== HISTÓRICO =====

export const obterHistoricoAcabamento = async (idAcabamento: number): Promise<any[]> => {
  try {
    const { data } = await api.get(`/AcabamentoN1/${idAcabamento}/Historico`);
    return data?.items || [];
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter histórico");
    return [];
  }
};

/**
 * Reporte de 31/08 (item 6): grava só o nome da figura de textura, sem enviar arquivo — é assim
 * que o acabamento assume uma figura já carregada por outro acabamento da empresa (F05A-P3b).
 */
export const definirNomeFiguraAcabamento = async (
  idAcabamento: number,
  nomeArquivo: string
): Promise<RetornoNomeMidia | null> => {
  try {
    const { data } = await api.put(`/AcabamentoN1/${idAcabamento}/Textura/DefinirNomeFigura`, {
      tipoMidia: "figura",
      nomeArquivo,
    });
    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao gravar o nome do arquivo");
    return null;
  }
};
